/* ─────────────────────────────────────────────────────────────
   Ask Velur — a conversation with Velur herself.
   The page (/pages/ask-velur) and the small panel on the shop and product pages share this file.
   Velur is a person, not a company: she speaks in the first person, and the page looks like what it is —
   a conversation on a phone. Her face (the mark) and her name (the logo) at the top; her words in beige,
   yours in white; she types before she answers.
   · She speaks first: a few short messages, typed one after another, saying who she is — an AI, here to
     help the customer get to know her scents, rituals and objects.
   · Her answers come out of the haze word by word. When she recommends, her server sends real product cards
     (the size, scent or colour she chose, the price, stock, picture), shown under her words with "View" and
     "Add to basket"; and a few quick replies for the next step (the space, its size, the options).
     A plain-text answer still has whatever it names laid under it, as before.
   · Her name is never written in letters: wherever it appears in what she says, the logo stands in its place.
   · She can be spoken to (her, 2026-09-29: "اذا سجلت صوت بيفهم وبيرد علي"): the microphone records, a live
     waveform follows the voice, and her server writes down what she heard; then she answers aloud — her words
     appear as she says them, her face breathes with her voice, a soft light in her scent's colour with it.
   · She answers through a webhook (theme settings → Ask Velur). Until one is set, the page says she is
     coming; with ?preview it shows a preview conversation, for the house only.
   ───────────────────────────────────────────────────────────── */
(function(){
  'use strict';
  if (window.__askVelur) return; window.__askVelur = true;

  var SCENTS = [   /* the seven: their pages are /pages/<k>; c = the colour each scent page wears */
    { k:'eclo',  n:'Éclo',  f:'Floral',   c:'#c6b3b6' },
    { k:'zeli',  n:'Zéli',  f:'Citrus',   c:'#e2deb3' },
    { k:'baya',  n:'Baya',  f:'Fruity',   c:'#bc8974' },
    { k:'verle', n:'Verlé', f:'Herbal',   c:'#afac7e' },
    { k:'velou', n:'Velou', f:'Powdery',  c:'#c9beaf' },
    { k:'sabe',  n:'Sabé',  f:'Oriental', c:'#ac7b44' },
    { k:'ebo',   n:'Ébo',   f:'Woody',    c:'#5b5843' }
  ];
  /* the shop writes some scents family-first ("Floral Éclo"): on her cards the scent's own name is enough */
  var FAMILY = new RegExp('(^|·\\s*)(' + SCENTS.map(function(s){ return s.f; }).join('|') + ')\\s+(?=\\S)', 'gi');
  var vname = function(v){ return String(v || '').replace(FAMILY, '$1'); };
  /* colour arithmetic for the scent's tint */
  var rgb = function(h){ h = h.replace('#', ''); return [0, 2, 4].map(function(i){ return parseInt(h.substr(i, 2), 16); }); };
  var mixc = function(a, b, t){ var x = rgb(a), y = rgb(b); return 'rgb(' + x.map(function(v, i){ return Math.round(v * t + y[i] * (1 - t)); }).join(',') + ')'; };
  var lum = function(h){ var x = rgb(h).map(function(v){ v /= 255; return v <= .03928 ? v / 12.92 : Math.pow((v + .055) / 1.055, 2.4); });
    return .2126 * x[0] + .7152 * x[1] + .0722 * x[2]; };
  var REDUCED = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var norm = function(s){ return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase(); };
  var esc = function(s){ return String(s).replace(/[&<>"']/g, function(c){ return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]; }); };
  var LOGO = '', MARK = '';

  /* ── her ear and her voice: the few tools they need ─────── */
  var AC = window.AudioContext || window.webkitAudioContext;
  /* how this browser can listen (her, 2026-09-29: "مخفي من الموبيل انو سجل"): the microphone and the phone's own
     recorder; or the microphone and the raw sound, made into a WAV here; or, with no microphone to reach, the
     browser's own listening (it writes down the words itself). With none of them, the microphone still shows and
     says where she can be spoken to. */
  var GUM = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  var SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  var HOW = GUM && window.MediaRecorder ? 'rec' : GUM && AC ? 'wav' : SR ? 'sr' : '';
  var SVG_MIC = '<svg class="av-mic-g" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.5a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0v-5a3 3 0 0 1 3-3zM6.5 11a5.5 5.5 0 0 0 11 0M12 16.5V20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
  var SVG_X = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7l10 10M17 7L7 17" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
  var SVG_PAUSE = '<svg class="av-say__pause" viewBox="0 0 16 16" aria-hidden="true"><path d="M5.5 3.5v9M10.5 3.5v9" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
  var SVG_PLAY = '<svg class="av-say__play" viewBox="0 0 16 16" aria-hidden="true"><path d="M5.5 3.6v8.8a.5.5 0 0 0 .77.42l6.6-4.4a.5.5 0 0 0 0-.84l-6.6-4.4a.5.5 0 0 0-.77.42z" fill="currentColor"/></svg>';
  var bars = function(n){ var s = ''; for (var i = 0; i < n; i++) s += '<i></i>'; return s; };
  /* a tenth of a second of silence, as a sound file: played on the tap that starts a conversation by voice, it
     lets a phone play her answer later, when it arrives */
  var SILENT = null;
  function wavHead(n, rate){                                /* a WAV file of n samples, 16-bit, one channel */
    var b = new ArrayBuffer(44 + n * 2), d = new DataView(b), w = function(o, s){ for (var i = 0; i < s.length; i++) d.setUint8(o + i, s.charCodeAt(i)); };
    w(0, 'RIFF'); d.setUint32(4, 36 + n * 2, true); w(8, 'WAVE'); w(12, 'fmt '); d.setUint32(16, 16, true); d.setUint16(20, 1, true); d.setUint16(22, 1, true);
    d.setUint32(24, rate, true); d.setUint32(28, rate * 2, true); d.setUint16(32, 2, true); d.setUint16(34, 16, true); w(36, 'data'); d.setUint32(40, n * 2, true);
    return d;
  }
  function silence(){
    if (SILENT) return SILENT;
    return (SILENT = URL.createObjectURL(new Blob([wavHead(800, 8000).buffer], { type:'audio/wav' })));
  }
  /* the raw sound of your voice (where a phone has no recorder of its own), made into a small WAV at 16 kHz */
  function wav(chunks, rate){
    var n = 0, o = 0; chunks.forEach(function(c){ n += c.length; });
    var all = new Float32Array(n); chunks.forEach(function(c){ all.set(c, o); o += c.length; });
    var r = rate / 16000, m = Math.floor(n / r), d = wavHead(m, 16000);
    for (var i = 0; i < m; i++) { var a = Math.floor(i * r), z = Math.max(a + 1, Math.floor((i + 1) * r)), s = 0;
      for (var j = a; j < z; j++) s += all[j] || 0;
      var v = Math.max(-1, Math.min(1, s / (z - a))); d.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true); }
    return new Blob([d.buffer], { type:'audio/wav' });
  }
  /* how loud her voice is, forty times a second, read from the sound itself — her face and the light follow it */
  function envelope(ac, ab){
    return new Promise(function(res){
      var done = false, fin = function(x){ if (!done) { done = true; res(x); } };
      if (!ac) return fin(null);
      try {
        var p = ac.decodeAudioData(ab.slice(0), function(buf){
          var ch = buf.getChannelData(0), step = Math.max(1, Math.round(buf.sampleRate / 40)), n = Math.ceil(ch.length / step), env = new Float32Array(n), max = 0, i, j;
          for (i = 0; i < n; i++) { var s = 0, e = Math.min(ch.length, (i + 1) * step); for (j = i * step; j < e; j++) s += ch[j] * ch[j]; env[i] = Math.sqrt(s / Math.max(1, e - i * step)); if (env[i] > max) max = env[i]; }
          for (i = 0; i < n; i++) env[i] = Math.min(1, Math.pow(env[i] / ((max * .85) || 1), .8));
          fin({ env:env, dur:buf.duration });
        }, function(){ fin(null); });
        if (p && p.catch) p.catch(function(){ fin(null); });
      } catch (e) { fin(null); }
      setTimeout(function(){ fin(null); }, 4000);
    });
  }
  /* her words, each hidden until she says it (the logo too, where she names herself) */
  function vwords(root){
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, null), nodes = [], n, out = [];
    while ((n = walker.nextNode())) if (n.nodeType === 3 || (n.tagName === 'IMG' && n.classList.contains('av-lg'))) nodes.push(n);
    nodes.forEach(function(node){
      if (node.nodeType === 1) { node.classList.add('av-v'); out.push(node); return; }
      var frag = document.createDocumentFragment();
      node.nodeValue.split(/(\s+)/).forEach(function(p){
        if (!p) return;
        if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(p)); return; }
        var s = document.createElement('span'); s.className = 'av-v'; s.textContent = p; frag.appendChild(s); out.push(s);
      });
      node.parentNode.replaceChild(frag, node);
    });
    return out;
  }

  /* ── what she writes: a little markdown, safely ─────────── */
  function inline(s){
    s = esc(s);
    s = s.replace(/\[([^\]]+)\]\(((?:https?:\/\/|\/)[^\s)]+)\)/g, function(m, t, u){ return '<a href="' + u + '"' + (/^https?:/.test(u) && u.indexOf(location.host) < 0 ? ' target="_blank" rel="noopener"' : '') + '>' + t + '</a>'; });
    s = s.replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g, function(m, pre, u){ return pre + '<a href="' + u + '" target="_blank" rel="noopener">' + u.replace(/^https?:\/\/(www\.)?/, '') + '</a>'; });
    s = s.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>').replace(/__([^_]+)__/g, '<b>$1</b>');
    s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1$2').replace(/(^|\s)_([^_\n]+)_/g, '$1$2');   /* no italics, ever */
    s = s.replace(/`([^`]+)`/g, '$1');
    return s;
  }
  function markdown(text){
    var out = [], list = null, para = [];
    var flush = function(){ if (para.length) { out.push('<p>' + inline(para.join(' ')) + '</p>'); para = []; } };
    var close = function(){ if (list) { out.push('<ul>' + list.join('') + '</ul>'); list = null; } };
    String(text).replace(/\r/g, '').split('\n').forEach(function(line){
      var l = line.trim(), m = l.match(/^(?:[-*•]|\d+[.)])\s+(.*)$/), h = l.match(/^#{1,6}\s+(.*)$/);
      if (!l) { flush(); close(); return; }
      if (m) { flush(); (list = list || []).push('<li>' + inline(m[1]) + '</li>'); return; }
      if (h) { flush(); close(); out.push('<p><b>' + inline(h[1]) + '</b></p>'); return; }
      close(); para.push(l);
    });
    flush(); close();
    return out.join('');
  }
  /* one message a paragraph, the way a person writes on a phone (a list stays with the line before it) */
  function paras(text){
    var out = [];
    String(text || '').replace(/\r/g, '').split(/\n\s*\n/).forEach(function(b){
      b = b.trim(); if (!b) return;
      if (out.length && /^(?:[-*•]|\d+[.)])\s+/.test(b)) out[out.length - 1] += '\n' + b; else out.push(b);
    });
    return out;
  }
  /* her name is always the logo, never letters */
  function logoize(root){
    if (!LOGO) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null), nodes = [], n;
    while ((n = walker.nextNode())) if (/\bvelur\b/i.test(n.nodeValue)) nodes.push(n);
    nodes.forEach(function(node){
      var frag = document.createDocumentFragment();
      node.nodeValue.split(/\b(velur)\b/i).forEach(function(p, i){
        if (i % 2) { var img = document.createElement('img'); img.className = 'av-lg'; img.src = LOGO; img.alt = 'Velur'; frag.appendChild(img); }
        else if (p) frag.appendChild(document.createTextNode(p));
      });
      node.parentNode.replaceChild(frag, node);
    });
  }
  function words(root){                                  /* each word out of the haze, one after another */
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, null), nodes = [], n, i = 0;
    while ((n = walker.nextNode())) if (n.nodeType === 3 || (n.tagName === 'IMG' && n.classList.contains('av-lg'))) nodes.push(n);
    nodes.forEach(function(node){
      if (node.nodeType === 1) { node.classList.add('av-w'); node.style.setProperty('--d', Math.min(i++ * 26, 3200) + 'ms'); return; }
      var parts = node.nodeValue.split(/(\s+)/), frag = document.createDocumentFragment();
      parts.forEach(function(p){
        if (!p) return;
        if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(p)); return; }
        var s = document.createElement('span'); s.className = 'av-w'; s.textContent = p;
        s.style.setProperty('--d', Math.min(i++ * 26, 3200) + 'ms'); frag.appendChild(s);
      });
      node.parentNode.replaceChild(frag, node);
    });
    return i;
  }
  var RTL = /[֐-ࣿ]/;
  function face(){ return '<span class="av-face" aria-hidden="true">' + (MARK ? '<img src="' + esc(MARK) + '" alt="">' : '') + '</span>'; }

  /* ── one conversation, wherever it is shown ─────────────── */
  var STORE = 'av:log:v1', SID = 'av:sid:v1';
  function sid(){ try { var s = localStorage.getItem(SID); if (!s) { s = 'v' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10); localStorage.setItem(SID, s); } return s; } catch (e) { return 'v-anon'; } }
  function loadLog(){ try { return JSON.parse(sessionStorage.getItem(STORE) || '[]'); } catch (e) { return []; } }
  function saveLog(l){ try { sessionStorage.setItem(STORE, JSON.stringify(l.slice(-40))); } catch (e) {} }

  /* is her server ready to answer — does it hold a key OpenAI accepts? (asked once, shared by everything) */
  var HEALTH = {};
  function health(webhook, fresh){
    var base = String(webhook || '').replace(/\/ask\/?$/, '');
    if (!base) return Promise.resolve(null);
    if (fresh || !HEALTH[base]) HEALTH[base] = fetch(base + '/health', { cache:'no-store' }).then(function(r){ return r.json(); }).catch(function(){ return null; });
    return HEALTH[base];
  }
  var isReady = function(h){ return !!(h && h.key && h.key_accepted !== false); };

  function Chat(root){
    var me = this; this.root = root; this.cfg = {};
    try { this.cfg = JSON.parse(root.querySelector('[data-av-config]').textContent); } catch (e) {}
    LOGO = this.cfg.logo || LOGO; MARK = this.cfg.mark || MARK;
    this.mode = root.classList.contains('av--panel') ? 'panel' : 'page';
    this.log = root.querySelector('.av__log'); this.stage = root.querySelector('.av__stage'); this.head = root.querySelector('.av__head');
    this.composer = root.querySelector('.av__composer'); this.input = root.querySelector('.av__input'); this.send = root.querySelector('.av__send');
    this.chips = root.querySelector('.av__chips'); this.chipsInit = this.chips ? this.chips.innerHTML : '';
    this.cover = root.querySelector('[data-av-cover]');
    if (this.cover) { var door = this.cover.querySelector('[data-av-enter]'); if (door) door.addEventListener('click', function(){ me.enter(); }); }
    if (!this.cfg.webhook && /[?&]preview(=|&|$)/.test(location.search)) this.cfg.preview = true;   /* the house may look at it before it is connected */
    this.keys();
    this.busy = false; this.greeting = null;
    /* with her server named, she is live only once it holds its key — until then a customer reads that she
       will be here very soon, never an error; she comes to life on her own the moment the key is given */
    this.live = !!this.cfg.preview;
    this.waiting(!this.live);
    if (this.cfg.webhook && !this.cfg.preview) this.watch(false);
    this.input.addEventListener('input', function(){ me.grow(); var has = !!me.input.value.trim(); me.root.classList.toggle('has-text', has);
      me.send.disabled = !me.live || me.busy || (!has && !(me.speaking && me.speaking.on)); });
    this.input.addEventListener('keydown', function(e){ if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) { e.preventDefault(); me.ask(); } });
    /* the round button sends what is written — or, while she listens, what was said */
    this.send.addEventListener('click', function(){
      if (me.listening) me.unlisten(true);
      else if (me.speaking && me.speaking.on && !me.input.value.trim()) me.hush();
      else me.ask(); });
    this.starters = root.querySelector('.av__starters');
    /* a tap on a next step keeps the conversation as it is: spoken, if they were speaking to her */
    [this.chips, this.starters].forEach(function(box){ if (box) box.addEventListener('click', function(e){
      var c = e.target.closest('.av__chip'); if (!c || !me.live || me.busy) return;
      var aloud = !!me.vmode && !c.classList.contains('av__tile');
      if (aloud) me.unlock();
      me.ask(c.textContent.trim(), { voice:aloud }); }); });
    addEventListener('keydown', function(e){ if (e.key === 'Escape' && me.listening) me.unlisten(false); });
    root.querySelectorAll('.av__new').forEach(function(nw){ nw.addEventListener('click', function(){ me.shut(); me.reset(); }); });
    this.menu(); this.voice();
    this.send.disabled = true;
    if (this.mode === 'page') this.follow();
    /* the arrow back: to the page they came from on this site, or else to the shop */
    root.querySelectorAll('[data-av-back]').forEach(function(a){ a.addEventListener('click', function(e){
      if (document.referrer && document.referrer.indexOf(location.origin) === 0 && history.length > 1) { e.preventDefault(); history.back(); } }); });
    this.keyField();
    this.restore();
    requestAnimationFrame(function(){ requestAnimationFrame(function(){ root.classList.add('is-in'); }); });
  }
  /* her name stays just under the site's header — and moves up with it when it hides on a phone, so nothing of
     the conversation shows above it */
  Chat.prototype.follow = function(){
    var root = this.root, head = document.getElementById('head');
    if (!head) return;
    var set = function(){
      var r = head.getBoundingClientRect(), pos = getComputedStyle(head).position;
      root.style.setProperty('--av-top', ((pos === 'fixed' || pos === 'sticky') ? Math.max(0, Math.round(r.bottom)) : 0) + 'px');
    };
    var until = 0, loop = function(){ set(); if (performance.now() < until) requestAnimationFrame(loop); };
    addEventListener('scroll', function(){ var go = performance.now() > until; until = performance.now() + 800; if (go) requestAnimationFrame(loop); }, { passive:true });
    addEventListener('resize', set);
    set();
  };
  /* the phone's menu: start again, a person, the site's pages */
  Chat.prototype.menu = function(){
    var me = this, btn = this.root.querySelector('.av__menu-btn'), box = this.root.querySelector('.av__menu');
    if (!btn || !box) return;
    this.shut = function(){ if (box.hidden) return; box.classList.remove('is-open'); btn.setAttribute('aria-expanded', 'false');
      setTimeout(function(){ box.hidden = true; }, REDUCED ? 0 : 320); };
    btn.addEventListener('click', function(){ box.hidden = false; btn.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(function(){ requestAnimationFrame(function(){ box.classList.add('is-open'); }); }); });
    var x = box.querySelector('.av__menu-x'); if (x) x.addEventListener('click', function(){ me.shut(); });
    box.addEventListener('click', function(e){ if (e.target === box) me.shut(); });
    addEventListener('keydown', function(e){ if (e.key === 'Escape') me.shut(); });
  };
  Chat.prototype.shut = function(){};
  /* ── speaking with her ──────────────────────────────────────
     Tap the microphone: the box becomes a live waveform of your voice (a cross to let it go, the time, the round
     button to send). A pause of a moment and a half after you speak sends it by itself. What you said appears
     at once as a small voice note, which becomes your words once she has heard them; then she answers aloud. */
  Chat.prototype.voice = function(){
    var me = this, btn = this.root.querySelector('.av__mic'), field = this.root.querySelector('.av__field');
    if (!btn || !field || !this.cfg.webhook) return;
    /* the microphone appears once her server says she can hear (her ear and her voice are on it) — on every phone:
       where this browser cannot listen at all, a tap says where she can be spoken to */
    health(this.cfg.webhook, false).then(function(h){
      if (h && h.voice && h.voice.hear) btn.hidden = false;
      /* ?avdebug in the address: what this phone can do, in the small print (for the house, to see why) */
      if (/[?&]avdebug/.test(location.search)) {
        var ua = navigator.userAgent, yn = function(x){ return x ? 'yes' : 'NO'; };
        var d = 'voice · server ' + yn(h && h.voice) + ' · microphone ' + yn(GUM) + ' · recorder ' + yn(window.MediaRecorder) + ' · sound ' + yn(AC) +
          ' · listening ' + yn(SR) + ' · way: ' + (HOW || 'none') + ' · ' + ((ua.match(/OS (\d+)_(\d+)/) || [])[0] || (ua.match(/Android \d+/) || [])[0] || '') +
          ' ' + ((ua.match(/Instagram|FBAN|FBAV|WhatsApp|Line\/|; wv\)|CriOS|FxiOS|EdgiOS|Version\/[\d.]+ Mobile.*Safari|Chrome\/\d+/) || [])[0] || '');
        me.noteHTML = esc(d); me.note(null);
      }
    });
    btn.addEventListener('click', function(){ me.listen(); });
    var rec = document.createElement('div'); rec.className = 'av__rec'; rec.hidden = true;
    rec.innerHTML = '<button class="av__rec-x" type="button" aria-label="' + esc(this.cfg.cancel || 'Let it go') + '">' + SVG_X + '</button>' +
      '<span class="av__rec-wave" aria-hidden="true">' + bars(34) + '</span>' +
      '<span class="av__rec-t" aria-hidden="true"><i class="av__rec-dot"></i><span>0:00</span></span>';
    field.insertBefore(rec, this.send);
    /* while she speaks, the round button under the thumb becomes a stop (her, 2026-09-29: "ما بقدر وقفها اذا هي عم تحكي") */
    this.send.insertAdjacentHTML('beforeend', '<svg class="av-stop-g" viewBox="0 0 16 16" aria-hidden="true"><rect x="4" y="4" width="8" height="8" rx="1.6" fill="currentColor"/></svg>');
    this.sendLabel = this.send.getAttribute('aria-label') || 'Send';
    rec.querySelector('.av__rec-x').addEventListener('click', function(){ me.unlisten(false); });
    this.rec = rec; this.recBars = [].slice.call(rec.querySelectorAll('.av__rec-wave i')); this.recT = rec.querySelector('.av__rec-t span');
    /* the light that follows her voice, from the top of the screen (yours is the ring round the box you speak into) */
    this.aura = document.createElement('div'); this.aura.className = 'av__aura'; this.aura.setAttribute('aria-hidden', 'true');
    this.root.appendChild(this.aura);
    document.addEventListener('visibilitychange', function(){ if (document.hidden) { me.unlisten(false); me.hush(); } });
  };
  Chat.prototype.base = function(){ return String(this.cfg.webhook || '').replace(/\/ask\/?$/, ''); };
  Chat.prototype.audio = function(){                        /* one sound context: to see a voice, and to read hers */
    if (this.ac === undefined) { try { this.ac = AC ? new AC() : null; } catch (e) { this.ac = null; } }
    return this.ac;
  };
  /* on the tap itself: the player her voice will use later, woken now (a phone allows sound only after a tap) */
  Chat.prototype.unlock = function(){
    var me = this, p = this.player;
    if (!p) {
      p = this.player = new Audio(); p.preload = 'auto'; p.setAttribute('playsinline', '');
      p.addEventListener('ended', function(){ var sp = me.speaking; if (sp && sp.on && sp.k >= 0) { me.said(sp, sp.parts[sp.k].to); me.playPart(sp, sp.k + 1); } });
    }
    if (this.woken) return;
    try { p.src = silence(); var r = p.play(); if (r && r.then) r.then(function(){ me.woken = true; }).catch(function(){}); else this.woken = true; } catch (e) {}
  };
  /* her small words under the box, in the language of the conversation (her, 2026-09-29: "بدي تفهمني اذا حكيت عربي
     انكليزي"): the language of what was last said or written to her — before anything, the phone's own language */
  var WORDS = {
    listening: ['I’m listening…', 'أسمعكم…'],
    unheard:   ['I didn’t quite hear you. Try again, or write to me.', 'لم أسمعكم جيدًا. أعيدوا المحاولة، أو اكتبوا لي.'],
    nomic:     ['I can’t reach your microphone here. You can write to me instead.', 'لا أستطيع الوصول إلى الميكروفون هنا. يمكنكم أن تكتبوا لي.'],
    tired:     ['I’ve listened to so many voices today. Write to me instead.', 'استمعت إلى أصوات كثيرة اليوم. اكتبوا لي، وسأجيبكم.'],
    nobrowser: ['To speak with me, open this page in Safari or Chrome. Here, you can write to me.', 'لتحدّثوني بصوتكم، افتحوا هذه الصفحة في Safari أو Chrome. وهنا يمكنكم أن تكتبوا لي.']
  };
  Chat.prototype.arabic = function(){
    var l = loadLog(); for (var i = l.length - 1; i >= 0; i--) if (l[i].who === 'you') return RTL.test(l[i].text);
    return /^ar/i.test(navigator.language || '');
  };
  Chat.prototype.words = function(k){ var ar = this.arabic(); return (!ar && this.cfg[k]) || WORDS[k][ar ? 1 : 0]; };
  /* a word in the small print under the box, for a moment ("I'm listening…") */
  Chat.prototype.note = function(msg, ms){
    var n = this.root.querySelector('.av__note'); if (!n) return;
    if (this.noteHTML == null) this.noteHTML = n.innerHTML;
    clearTimeout(this.noteT);
    if (!msg) { n.innerHTML = this.noteHTML; n.classList.remove('is-status'); n.removeAttribute('dir'); return; }
    n.textContent = msg; n.classList.add('is-status'); n.dir = RTL.test(msg) ? 'rtl' : 'ltr';
    if (ms) { var me = this; this.noteT = setTimeout(function(){ me.note(null); }, ms); }
  };
  Chat.prototype.glow = function(lv, who){                  /* the light, as loud as the voice */
    var a = this.aura; if (!a) return;
    if (who) a.setAttribute('data-who', who);
    a.style.opacity = who ? (.3 + Math.min(1, lv) * .7).toFixed(3) : '0';
  };

  Chat.prototype.listen = function(){
    var me = this;
    if (!this.live || this.busy || this.listening) return;
    if (!HOW) { this.note(this.words('nobrowser'), 7000); return; }
    this.hush(); this.hurry(); this.unlock();
    var ac = HOW === 'sr' ? null : this.audio(); if (ac && ac.state === 'suspended' && ac.resume) ac.resume();
    var L = this.listening = { levels:[], chunks:[], heard:false, lv:0, t0:performance.now(), loudAt:0 };
    this.root.classList.add('is-listening'); this.rec.hidden = false; this.send.disabled = false;
    this.recBars.forEach(function(b){ b.style.transform = ''; }); this.recT.textContent = '0:00';
    this.note(this.words('listening'));
    var nomic = function(){ if (me.listening !== L) return;
      me.unlisten(false); me.note(me.words('nomic'), 6000); };
    if (HOW === 'sr') { this.hark(L, nomic); return; }
    navigator.mediaDevices.getUserMedia({ audio:{ echoCancellation:true, noiseSuppression:true, autoGainControl:true } }).then(function(stream){
      if (me.listening !== L) { stream.getTracks().forEach(function(t){ t.stop(); }); return; }
      L.stream = stream;
      if (ac) { try { L.src = ac.createMediaStreamSource(stream); L.an = ac.createAnalyser(); L.an.fftSize = 1024; L.src.connect(L.an); L.buf = new Float32Array(L.an.fftSize); } catch (e) { L.an = null; } }
      if (HOW === 'rec') {
        var type = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'].filter(function(t){
          return MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t); })[0];
        var r = L.recorder = type ? new MediaRecorder(stream, { mimeType:type }) : new MediaRecorder(stream);
        r.ondataavailable = function(e){ if (e.data && e.data.size) L.chunks.push(e.data); };
        r.onstop = function(){ me.heard(L); };
        r.start(1000);
      } else {                                                  /* no recorder: the raw sound, kept as it comes */
        if (!L.src) throw new Error('no sound');
        var node = L.proc = ac.createScriptProcessor(4096, 1, 1); L.pcm = []; L.rate = ac.sampleRate;
        node.onaudioprocess = function(e){ if (me.listening === L) L.pcm.push(new Float32Array(e.inputBuffer.getChannelData(0))); };
        L.src.connect(node); node.connect(ac.destination);
      }
      L.t0 = performance.now();
      me.meter(L);
    }).catch(nomic);
  };
  /* where there is no microphone to reach: the browser's own listening, which writes down the words itself */
  Chat.prototype.hark = function(L, nomic){
    var me = this, r;
    try { r = L.sr = new SR(); } catch (e) { nomic(); return; }
    var arabic = this.arabic();
    r.lang = arabic ? 'ar-AE' : (navigator.language || 'en-US'); r.interimResults = true; r.continuous = false; r.maxAlternatives = 1;
    L.said = '';
    r.onresult = function(e){ var t = ''; for (var i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
      L.said = t.trim(); L.heard = true; L.loudAt = performance.now(); };
    r.onerror = function(e){ L.err = e.error || 'error'; };
    r.onend = function(){ if (/allowed/.test(L.err || '') && !L.said) { nomic(); me.heard(L); return; }
      if (me.listening === L) me.unlisten(!!L.said); me.heard(L); };
    try { r.start(); } catch (e) { nomic(); return; }
    this.meter(L);
  };
  /* while you speak: the waveform moves with your voice, the time counts, and a pause sends it */
  Chat.prototype.meter = function(L){
    var me = this, last = 0, field = this.root.querySelector('.av__field');
    var tick = function(now){
      if (me.listening !== L) { if (field) field.style.removeProperty('--lv'); return; }
      var v = 0;
      if (L.an) { L.an.getFloatTimeDomainData(L.buf); var s = 0; for (var i = 0; i < L.buf.length; i++) s += L.buf[i] * L.buf[i];
        v = Math.max(0, Math.min(1, (Math.sqrt(s / L.buf.length) - .01) * 8));
        if (v > .16) { L.heard = true; L.loudAt = now; } }
      /* no way to see the voice (the browser's own listening): a quiet breath, and a lift each time words arrive */
      else v = .18 + .14 * Math.sin(now / 180) * Math.sin(now / 470) + (now - L.loudAt < 350 ? .45 : 0);
      L.lv = L.lv * .55 + v * .45;
      var t = (now - L.t0) / 1000;
      if (now - last > 60) {
        last = now; L.levels.push(L.lv);
        var lv = L.levels, nb = me.recBars.length;
        for (var k = 0; k < nb; k++) { var x = lv[lv.length - nb + k]; me.recBars[k].style.transform = 'scaleY(' + (x == null ? .1 : (.1 + Math.min(1, x * 1.3) * .9)).toFixed(3) + ')'; }
        me.recT.textContent = Math.floor(t / 60) + ':' + ('0' + Math.floor(t % 60)).slice(-2);
      }
      if (!REDUCED && field) field.style.setProperty('--lv', L.lv.toFixed(3));
      /* a moment of quiet after words (about a second and a half), or a whole minute: sent */
      if ((L.an && L.heard && t > 1 && now - L.loudAt > 1400) || t > 60) { me.unlisten(true); return; }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  Chat.prototype.unlisten = function(send){
    var L = this.listening; if (!L) return;
    this.listening = null; L.send = !!send;
    this.root.classList.remove('is-listening'); this.rec.hidden = true; this.note(null); this.glow(0, null);
    this.send.disabled = !this.input.value.trim();
    if (L.recorder && L.recorder.state !== 'inactive') { try { L.recorder.stop(); } catch (e) { this.heard(L); } }
    else if (L.sr) {                                           /* its last words come as it stops */
      var me = this; try { if (send) L.sr.stop(); else L.sr.abort(); } catch (e) { this.heard(L); }
      setTimeout(function(){ me.heard(L); }, 2500);
    }
    else this.heard(L);
  };
  /* what you said: shown at once as a small voice note, sent to her ear, and turned into your words */
  Chat.prototype.heard = function(L){
    var me = this;
    if (L.done) return; L.done = true;
    if (L.stream) L.stream.getTracks().forEach(function(t){ t.stop(); });          /* the phone's microphone light goes out */
    if (L.src) try { L.src.disconnect(); } catch (e) {}
    if (L.proc) try { L.proc.onaudioprocess = null; L.proc.disconnect(); } catch (e) {}
    if (this.ac && this.ac.suspend && this.ac.state === 'running') this.ac.suspend();
    if (!L.send) return;
    var unheard = this.words('unheard'), type = '', blob = null;
    if (L.sr) { if (!L.said) { this.note(unheard, 5000); return; } }
    else {
      if (L.pcm) { type = 'audio/wav'; blob = wav(L.pcm, L.rate || 48000); }
      else { type = (L.recorder && L.recorder.mimeType) || 'audio/webm'; blob = new Blob(L.chunks, { type:type }); }
      if (blob.size < 1500 || (L.an && !L.heard)) { this.note(unheard, 5000); return; }
    }
    this.hurry(); this.talking(true); this.root.classList.remove('has-replies');
    this.busy = true; this.send.disabled = true;
    /* the voice note: your own waveform, gently shimmering while she listens to it */
    var q = this.mine('', true, true), b = q.querySelector('.av-bub'), lv = L.levels.slice(-240), nb = 26, h = '';
    for (var i = 0; i < nb; i++) { var a = Math.floor(i * lv.length / nb), z = Math.floor((i + 1) * lv.length / nb), m = 0;
      for (var j = a; j < Math.max(z, a + 1); j++) m = Math.max(m, lv[j] || 0);
      h += '<i style="transform:scaleY(' + (.18 + Math.min(1, m * 1.3) * .82).toFixed(3) + ');animation-delay:' + (i * 45) + 'ms"></i>'; }
    b.classList.add('is-hearing'); b.innerHTML = SVG_MIC + '<span class="av-note-wave" aria-hidden="true">' + h + '</span>';
    requestAnimationFrame(function(){ me.reveal(q, 'end', true); });
    var got = function(text){ me.busy = false; me.saidBy(b, text); me.ask(text, { voice:true, spoken:true, q:q }); };
    if (L.sr) { setTimeout(function(){ got(L.said); }, 500); return; }        /* the browser has already written it down */
    fetch(this.base() + '/hear', { method:'POST', headers:{ 'Content-Type':type }, body:blob })
      .then(function(r){ return r.json().then(function(j){ return { ok:r.ok, status:r.status, j:j }; }); })
      .then(function(res){
        var text = String((res.j && res.j.text) || '').trim();
        if (!res.ok || !text) throw res;
        got(text);
      })
      .catch(function(res){
        q.remove(); me.busy = false; me.send.disabled = !me.input.value.trim();
        if (!me.log.querySelector('.av-msg--you')) me.talking(false);
        me.note(res && res.status === 429 ? me.words('tired') : me.words('unheard'), 5500);
      });
  };
  /* your words, in place of the voice note (a small microphone keeps saying they were spoken) */
  Chat.prototype.saidBy = function(b, text){
    b.classList.remove('is-hearing'); b.classList.add('is-said');
    b.innerHTML = SVG_MIC; var s = document.createElement('span'); s.textContent = text; b.appendChild(s); b.dir = 'auto';
  };

  /* ── she answers aloud ──────────────────────────────────────
     Her server says her answer in a few parts; they are fetched at once and played one after another. Each word
     appears as she reaches it; the cards come as she finishes; a small round control under her words pauses her,
     or lets you hear her again. */
  Chat.prototype.voiceOf = function(v){
    var me = this, base = this.base(), ac = this.audio(), total = 0;
    var sp = { parts:[], k:-1, on:false, lv:0, shown:0, words:[] };
    v.parts.forEach(function(w, k){
      total += w || 1;
      var P = { w:w || 1, url:null, env:null, dur:0 };
      P.ready = fetch(base + '/voice/' + v.id + '/' + k)
        .then(function(r){ if (!r.ok) throw new Error('voice'); return r.arrayBuffer(); })
        .then(function(ab){
          P.url = URL.createObjectURL(new Blob([ab], { type:'audio/mpeg' }));
          return envelope(ac, ab).then(function(e){ if (e) { P.env = e.env; P.dur = e.dur; } return true; });
        }).catch(function(){ return false; });
      sp.parts.push(P);
    });
    sp.total = total;
    /* she begins when her first words are ready — or, if they are not within a few seconds, simply writes */
    sp.ready = Promise.race([sp.parts[0].ready, new Promise(function(r){ setTimeout(function(){ r(false); }, 10000); })]);
    return sp;
  };
  Chat.prototype.begin = function(sp, g, a){
    var me = this;
    sp.g = g; sp.a = a; sp.face = g.el.querySelector('.av-face');
    sp.rate = RTL.test(a.text) ? 1.15 : 1;                     /* her Arabic voice is slower than her English: a little quicker */
    sp.words = vwords(g.col); sp.shown = 0;
    var n = sp.words.length, acc = 0;
    sp.parts.forEach(function(P){ P.from = Math.round(acc / sp.total * n); acc += P.w; P.to = Math.round(acc / sp.total * n); });
    sp.cardsAt = Math.ceil(n * .7);
    /* the small round control: pause her, or hear her again */
    var c = sp.ctl = document.createElement('button'); c.type = 'button'; c.className = 'av-say';
    c.setAttribute('aria-label', this.cfg.pause || 'Pause');
    c.innerHTML = SVG_PAUSE + SVG_PLAY + '<span class="av-say__bars" aria-hidden="true">' + bars(5) + '</span>';
    c.addEventListener('click', function(){ if (sp.on) me.hush(); else me.replay(sp); });
    g.col.appendChild(c); sp.bars = [].slice.call(c.querySelectorAll('.av-say__bars i'));
    this.speaking = sp;
    if (REDUCED) { this.said(sp, n); }
    this.voiceOn(sp);
    this.playPart(sp, 0);
  };
  Chat.prototype.voiceOn = function(sp){
    var me = this;
    sp.on = true; sp.g.el.classList.add('is-speaking'); this.root.classList.add('is-speaking');
    this.send.disabled = false; if (!this.input.value.trim()) this.send.setAttribute('aria-label', this.arabic() ? 'إيقاف' : 'Stop');
    sp.ctl.classList.remove('is-done'); sp.ctl.setAttribute('aria-label', this.cfg.pause || 'Pause');
    var step = function(){ if (me.speaking !== sp || !sp.on) return; me.tick(sp); requestAnimationFrame(step); };
    requestAnimationFrame(step);
  };
  Chat.prototype.playPart = function(sp, k){
    var me = this;
    if (this.speaking !== sp || !sp.on) return;
    if (k >= sp.parts.length) { this.finish(sp); return; }
    var P = sp.parts[k];
    P.ready.then(function(ok){
      if (me.speaking !== sp || !sp.on) return;
      if (!ok || !P.url) { me.said(sp, P.to); me.playPart(sp, k + 1); return; }          /* a part that would not come: its words, silently */
      sp.k = k; var p = me.player;
      p.defaultPlaybackRate = sp.rate || 1;
      p.src = P.url;
      p.playbackRate = sp.rate || 1;
      var r; try { r = p.play(); } catch (e) { r = null; me.finish(sp); }
      if (r && r.catch) r.catch(function(){ if (me.speaking === sp && sp.k === k) me.finish(sp); });   /* the phone would not let her speak: her words, and the control to hear her */
    });
  };
  /* her words up to the nth come clear; the cards come as she finishes */
  Chat.prototype.said = function(sp, upto){
    upto = Math.min(sp.words.length, upto);
    for (var i = sp.shown; i < upto; i++) { var w = sp.words[i]; w.classList.add('is-on'); }
    if (upto > sp.shown) sp.shown = upto;
    if (!sp.cards && sp.shown >= sp.cardsAt) { sp.cards = true; this.show(sp.g.col, sp.a.text, sp.a.products, true, 0); }
  };
  Chat.prototype.tick = function(sp){
    var p = this.player, P = sp.parts[sp.k]; if (!P) return;
    var t = p.currentTime || 0, d = P.dur || (isFinite(p.duration) && p.duration) || P.w * .4, n = P.to - P.from;
    if (!p.paused) this.said(sp, P.from + Math.min(n, Math.floor(t / (d * .94) * n) + 1));
    var v = p.paused ? 0 : P.env ? P.env[Math.min(P.env.length - 1, Math.floor(t * 40))] : .45 + .3 * Math.sin(t * 9) * Math.sin(t * 3.1);
    sp.lv = sp.lv * .5 + v * .5;
    if (REDUCED) return;
    if (sp.face) sp.face.style.setProperty('--lv', sp.lv.toFixed(3));
    for (var i = 0; i < sp.bars.length; i++) {                      /* five little bars, the voice passing through them */
      var e = P.env ? P.env[Math.max(0, Math.min(P.env.length - 1, Math.floor((t - Math.abs(i - 2) * .07) * 40)))] : sp.lv;
      sp.bars[i].style.transform = 'scaleY(' + (.22 + (p.paused ? 0 : e) * .78).toFixed(3) + ')';
    }
    this.glow(sp.lv, 'her');
  };
  Chat.prototype.finish = function(sp){
    if (!sp.g) return;
    var was = sp.on; sp.on = false; sp.k = -1;
    this.said(sp, sp.words.length);
    if (!sp.cards) { sp.cards = true; this.show(sp.g.col, sp.a.text, sp.a.products, true, 0); }
    if (!sp.replied) { sp.replied = true; this.suggest(sp.a.replies); }
    sp.g.el.classList.remove('is-speaking'); this.root.classList.remove('is-speaking');
    this.send.setAttribute('aria-label', this.sendLabel || 'Send'); this.send.disabled = this.busy || !this.input.value.trim();
    if (sp.face) sp.face.style.removeProperty('--lv');
    sp.bars.forEach(function(b){ b.style.transform = ''; });
    sp.ctl.classList.add('is-done'); sp.ctl.setAttribute('aria-label', this.cfg.again || 'Hear her again');
    if (was || this.speaking === sp) this.glow(0, null);
  };
  Chat.prototype.hush = function(){                          /* she stops: her words all shown, the rest as if said */
    var sp = this.speaking;
    if (sp && sp.on) { try { this.player.pause(); } catch (e) {} this.finish(sp); }
  };
  Chat.prototype.replay = function(sp){
    if (this.busy || this.listening) return;
    this.hush(); this.unlock();
    this.speaking = sp; this.voiceOn(sp); this.playPart(sp, 0);
  };
  /* the scent she is speaking of — the first one her words name, else the scent of the first card she shows */
  Chat.prototype.tone = function(text, products){
    var n = norm(text), best = null, at = Infinity;
    var has = function(s, str){ return new RegExp('(^|[^a-z])' + s.k + '([^a-z]|$)').exec(str); };
    SCENTS.forEach(function(s){ var m = has(s, n); if (m && m.index < at) { at = m.index; best = s; } });
    if (!best && Array.isArray(products)) products.some(function(p){ var v = norm(p.variant || '');
      return SCENTS.some(function(s){ if (has(s, v)) { best = s; return true; } return false; }); });
    return best;
  };
  /* the conversation takes that scent's colour: a tint for her words, the colour itself for the send button (a
     little deeper when it is too pale for a white arrow), and a soft glow at the top; null gives back the beige */
  Chat.prototype.paint = function(s){
    var st = this.root.style;
    if (!s || !s.c) { ['--av-her', '--av-pill', '--av-send', '--av-wash', '--av-rgb'].forEach(function(p){ st.removeProperty(p); }); this.root.removeAttribute('data-scent'); return; }
    var k = lum(s.c) < .15 ? .62 : 1;   /* a dark scent (Ébo) is laid on more lightly, or it turns grey */
    st.setProperty('--av-her', mixc(s.c, '#ffffff', .30 * k));
    st.setProperty('--av-pill', mixc(s.c, '#ffffff', .18 * k));
    st.setProperty('--av-wash', mixc(s.c, '#ffffff', .42 * k));
    var send = lum(s.c) > .42 ? mixc(s.c, '#1c1b18', .72) : mixc(s.c, s.c, 1);
    st.setProperty('--av-send', send);
    st.setProperty('--av-rgb', send.replace(/^rgb\(|\)$/g, ''));      /* her voice's light and waveforms, in the same colour */
    this.root.setAttribute('data-scent', s.k);
  };
  Chat.prototype.waiting = function(on){
    var soon = this.root.querySelector('.av__soon'), note = this.root.querySelector('.av__note');
    this.root.classList.toggle('is-waiting', on);
    this.input.disabled = on; this.send.disabled = on || !this.input.value.trim();
    if (soon) soon.hidden = !on; if (note) note.hidden = on;
  };
  Chat.prototype.watch = function(fresh){
    var me = this; clearTimeout(this.again);
    health(this.cfg.webhook, fresh).then(function(h){
      if (isReady(h)) { me.live = true; me.waiting(false); return; }
      me.again = setTimeout(function(){ if (!document.hidden) me.watch(true); else me.watch(false); }, 45000);
    });
  };
  /* the owner's field for the first OpenAI key: shown only when her server has none, and only to her
     (the markup is rendered for the settings door alone). The key goes straight to her server. */
  Chat.prototype.keyField = function(){
    var box = this.root.querySelector('[data-av-key]');
    if (!box || !this.cfg.webhook) return;
    var me = this, base = this.cfg.webhook.replace(/\/ask\/?$/, ''), input = box.querySelector('.av-key__in'), go = box.querySelector('.av-key__go'),
        row = box.querySelector('.av-key__row'), msg = box.querySelector('.av-key__msg');
    var say = function(t, ok){ msg.textContent = t; msg.classList.toggle('is-ok', !!ok); };
    health(this.cfg.webhook, false).then(function(h){
      if (!h || isReady(h)) return;
      box.hidden = false;
      if (h.key) { row.hidden = true; say('OpenAI refuses the key that is on your server. It can be changed there.'); return; }
      if (!h.key_open) { row.hidden = true; say('This page can no longer take the key. It can be given on the server.'); }
    });
    go.addEventListener('click', function(){
      var k = input.value.trim(); if (!k || go.disabled) return;
      go.disabled = true; say('Checking with OpenAI…');
      fetch(base + '/key', { method:'POST', headers:{ 'Content-Type':'application/json' }, body:JSON.stringify({ key:k }) })
        .then(function(r){ return r.json().then(function(j){ return { ok:r.ok, j:j }; }); })
        .then(function(res){
          if (res.ok) { input.value = ''; row.hidden = true; say('She has it, and she is ready. Talk to her below.', true); me.watch(true); setTimeout(function(){ box.hidden = true; }, 6000); }
          else { say((res.j && res.j.error) || 'That did not work.'); go.disabled = false; }
        })
        .catch(function(){ say('Your server could not be reached. Try again in a minute.'); go.disabled = false; });
    });
    input.addEventListener('keydown', function(e){ if (e.key === 'Enter') { e.preventDefault(); go.click(); } });
  };
  /* the words an answer might use for an object: its full name, and any distinctive word of it that at most
     three objects share (so "INKRANCE" brings the three sizes, and "Aurélle", shared by four, brings none) */
  Chat.prototype.keys = function(){
    var ps = this.cfg.products || [], count = {}, STOP = /^(large|medium|small|mini|reeds?|diffusers?|candles?|bottles?|sprays?|home|linen|fragrance|sand|bags?|wooden|wicks|box|with|for|the|and|set|ritual|electric|oil|oils|dropper)$/;
    ps.forEach(function(p){ p.w = norm(p.t).split(/[^a-z0-9]+/).filter(function(w){ return w.length >= 5 && !STOP.test(w); });
      p.w.forEach(function(w){ count[w] = (count[w] || 0) + 1; }); });
    ps.forEach(function(p){ p.k = p.w.filter(function(w){ return count[w] <= 3; }); });
  };
  Chat.prototype.grow = function(){ var i = this.input; i.style.height = 'auto'; i.style.height = Math.min(140, i.scrollHeight) + 'px'; };
  Chat.prototype.talking = function(on){ this.root.classList.toggle('is-talking', on); };
  Chat.prototype.ready = function(){ this.root.classList.add('is-ready'); };

  /* ── the pieces of a conversation ───────────────────────── */
  Chat.prototype.day = function(){
    if (!this.cfg.day) return;
    var d = document.createElement('p'); d.className = 'av-day'; d.textContent = this.cfg.day; this.log.appendChild(d);
  };
  Chat.prototype.her = function(){                        /* a group of her messages, her face beside it */
    var el = document.createElement('div'); el.className = 'av-msg av-msg--her';
    el.innerHTML = face() + '<div class="av-msg__col"></div>';
    this.log.appendChild(el);
    return { el:el, col:el.lastChild };
  };
  Chat.prototype.bubble = function(g, text){
    var b = document.createElement('div'); b.className = 'av-bub';
    b.innerHTML = markdown(text); if (RTL.test(text)) b.dir = 'rtl';
    logoize(b);
    g.col.appendChild(b);
    return b;
  };
  Chat.prototype.typing = function(g){                    /* she is writing */
    var b = document.createElement('div'); b.className = 'av-bub av-bub--typing'; b.setAttribute('role', 'status');
    b.setAttribute('aria-label', this.cfg.typing || 'Typing'); b.innerHTML = '<i></i><i></i><i></i>';
    g.col.appendChild(b);
    return b;
  };

  /* she introduces herself: a few short messages, typed one after another */
  Chat.prototype.greet = function(animate){
    var me = this, parts = paras(this.cfg.greeting);
    this.day();
    if (!parts.length) { this.ready(); return; }
    var g = this.her(); g.el.classList.add('av-msg--hello');
    if (this.root.classList.contains('av--soft')) {             /* one soft card: her title, and the rest under it */
      var card = function(){ var b = me.bubble(g, parts.join('\n\n')); b.classList.add('av-bub--hello'); return b; };
      if (!animate || REDUCED) { card().style.animation = 'none'; this.ready(); return; }
      var dots = this.typing(g), sc = this.greeting = { g:g, parts:[], i:0, timer:null, dots:dots };
      sc.timer = setTimeout(function(){
        if (me.greeting !== sc) return;
        dots.remove(); sc.dots = null; var n = words(card());
        sc.timer = setTimeout(function(){ if (me.greeting === sc) { me.greeting = null; me.ready(); } }, Math.min(1400, 300 + n * 26));
      }, 1100);
      return;
    }
    if (!animate || REDUCED) { parts.forEach(function(p){ me.bubble(g, p).style.animation = 'none'; }); this.ready(); return; }
    var st = this.greeting = { g:g, parts:parts, i:0, timer:null, dots:null };
    var step = function(){
      if (me.greeting !== st) return;
      if (st.i >= parts.length) { me.greeting = null; me.ready(); return; }
      st.dots = me.typing(g);
      var p = parts[st.i], wait = st.i ? Math.min(1500, 520 + p.length * 7) : 1000;
      st.timer = setTimeout(function(){
        st.dots.remove(); st.dots = null;
        var n = words(me.bubble(g, parts[st.i++]));
        st.timer = setTimeout(step, Math.min(1400, 380 + n * 26));
      }, wait);
    };
    st.timer = setTimeout(step, 450);
  };
  Chat.prototype.hurry = function(){                      /* the customer speaks before she has finished: she finishes at once */
    var st = this.greeting; if (!st) return;
    this.greeting = null; clearTimeout(st.timer); if (st.dots) st.dots.remove();
    if (!st.parts.length && this.root.classList.contains('av--soft') && !st.g.col.querySelector('.av-bub--hello')) {
      var b = this.bubble(st.g, paras(this.cfg.greeting).join('\n\n')); b.classList.add('av-bub--hello');
    }
    while (st.i < st.parts.length) this.bubble(st.g, st.parts[st.i++]);
    this.ready();
  };
  /* the front door: a page of her diary; she starts typing only once they come in */
  Chat.prototype.enter = function(){
    var me = this, c = this.cover;
    if (!c || c.hidden || c.classList.contains('is-leaving')) return;
    c.classList.add('is-leaving'); this.root.classList.add('is-entered');
    setTimeout(function(){ c.hidden = true; c.classList.remove('is-leaving'); window.scrollTo(0, 0); me.greet(true); }, REDUCED ? 0 : 650);
  };
  Chat.prototype.door = function(fade){
    var c = this.cover; if (!c) return;
    this.root.classList.remove('is-entered'); c.hidden = false;
    if (fade && !REDUCED) { c.classList.add('is-back'); requestAnimationFrame(function(){ requestAnimationFrame(function(){ c.classList.remove('is-back'); }); }); }
  };
  Chat.prototype.restore = function(){
    var me = this, l = loadLog(); this.log.innerHTML = '';
    if (!l.length && this.cover) { this.talking(false); this.door(false); return; }
    if (this.cover) { this.cover.hidden = true; this.root.classList.add('is-entered'); }
    this.greet(!l.length);
    if (!l.length) { this.talking(false); return; }
    this.talking(true);
    var els = l.map(function(m){ return me.render(m); });
    var last = l[l.length - 1]; if (last && last.who !== 'you') this.suggest(last.replies);
    for (var j = l.length - 1; j >= 0; j--) if (l[j].who !== 'you') { var tj = this.tone(l[j].text, l[j].products); if (tj) { this.paint(tj); break; } }
    var q = null; for (var i = l.length - 1; i >= 0; i--) if (l[i].who === 'you') { q = els[i]; break; }
    requestAnimationFrame(function(){ me.settle(q, els[els.length - 1], false); });
  };
  Chat.prototype.reset = function(){
    if (this.greeting) { clearTimeout(this.greeting.timer); this.greeting = null; }
    this.unlisten(false); this.hush(); this.speaking = null; this.vmode = false;
    saveLog([]); try { localStorage.removeItem(SID); } catch (e) {}
    this.paint(null);
    this.log.innerHTML = ''; this.talking(false); this.root.classList.remove('is-ready'); this.input.value = ''; this.grow(); this.root.classList.remove('has-text');
    if (this.chips) this.chips.innerHTML = this.chipsInit; this.root.classList.remove('has-replies');
    if (this.mode === 'panel') this.stage.scrollTo({ top:0, behavior:'smooth' });
    else window.scrollTo({ top:Math.max(0, scrollY + this.root.getBoundingClientRect().top), behavior:'smooth' });
    if (this.cover) this.door(true); else this.greet(true);
  };
  /* a message from the saved conversation, shown as it was */
  Chat.prototype.render = function(m){
    if (m.who === 'you') return this.mine(m.text, false, !!m.v);
    var me = this, g = this.her();
    paras(m.text).forEach(function(p){ me.bubble(g, p).style.animation = 'none'; });
    this.show(g.col, m.text, m.products, false, 0);
    return g.el;
  };
  Chat.prototype.mine = function(text, animate, spoken){
    var el = document.createElement('div'); el.className = 'av-msg av-msg--you' + (spoken ? ' av-msg--voice' : '');
    var b = document.createElement('div'); b.className = 'av-bub'; b.textContent = text; b.dir = 'auto'; el.appendChild(b);
    if (spoken && text) this.saidBy(b, text);
    if (!animate) b.style.animation = 'none';
    this.log.appendChild(el);
    return el;
  };

  /* ── where the conversation is on the screen ────────────── */
  Chat.prototype.top = function(){                        /* under the site's header and her name */
    if (this.mode === 'panel') return (this.head ? this.head.offsetHeight : 0) + 14;
    var h = document.getElementById('head'), t = 0;
    if (h) { var r = h.getBoundingClientRect(), pos = getComputedStyle(h).position; if ((pos === 'fixed' || pos === 'sticky') && r.bottom > 0) t = r.bottom; }
    var bar = this.root.querySelector('.av__top');
    return t + ((bar && bar.offsetHeight) || (this.head ? this.head.offsetHeight : 0)) + 14;
  };
  Chat.prototype.room = function(){
    var c = this.composer ? this.composer.offsetHeight : 0;
    return (this.mode === 'panel' ? this.stage.clientHeight : innerHeight) - this.top() - c;
  };
  /* bring a message into view: its top under her name, or its foot above the question box */
  Chat.prototype.reveal = function(el, where, smoothly){
    if (!el) return;
    var how = smoothly && !REDUCED ? 'smooth' : 'auto', r = el.getBoundingClientRect(), c = this.composer ? this.composer.offsetHeight : 0;
    if (this.mode === 'panel') {
      var box = this.stage, b = box.getBoundingClientRect();
      var y = where === 'start' ? box.scrollTop + r.top - b.top - this.top() : box.scrollTop + r.bottom - b.bottom + c + 14;
      box.scrollTo({ top:Math.max(0, y), behavior:how });
    } else {
      var yy = where === 'start' ? scrollY + r.top - this.top() : scrollY + r.bottom - innerHeight + c + 14;
      window.scrollTo({ top:Math.max(0, yy), behavior:how });
    }
  };
  /* after an answer: if the question and its answer fit, show them whole; if not, the question at the top,
     so the answer is read from its first word */
  Chat.prototype.settle = function(q, a, smoothly){
    if (!a) return;
    var span = a.getBoundingClientRect().bottom - (q || a).getBoundingClientRect().top;
    if (q && span > this.room() - 20) this.reveal(q, 'start', smoothly); else this.reveal(a, 'end', smoothly);
  };

  /* under her words: the cards she chose (from her server), or — for a plain answer — whatever it names */
  Chat.prototype.show = function(col, text, products, animate, d){
    if (Array.isArray(products)) {
      if (products.length) this.offer(col, products, animate, d);
      else this.lay(col, text, animate, d, true);        /* still asking her questions: only the scents she names */
    } else this.lay(col, text, animate, d, false);
  };
  /* her recommendations as a quiet list: the exact variant, its price and stock, why it suits them, two small words */
  /* her word, 2026-10-14: these read "Coming soon" while they cannot be bought — the same list as snippets/coming-soon.liquid */
  var SOON = ['hand-wash-500-ml', 'hand-lotion-500-ml'];
  Chat.prototype.offer = function(col, list, animate, d){
    var me = this, row = document.createElement('div'); row.className = 'av-offers';
    list.forEach(function(p, i){
      var c = document.createElement('div'), u = esc(p.url || ('/products/' + p.handle));
      var name = String(p.title || '').split(' - ')[0];          /* "Whisperaé Sprays - Home & Linen" → "Whisperaé Sprays" */
      c.className = 'av-offer'; c.style.setProperty('--d', (d + i * 160) + 'ms');
      if (!animate) { c.style.animation = 'none'; c.style.opacity = 1; }
      c.innerHTML = '<a class="av-offer__img" href="' + u + '" tabindex="-1">' + (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : '') + '</a>' +
        '<div class="av-offer__b"><a class="av-offer__n" href="' + u + '">' + esc(name) + '</a>' +
        '<div class="av-offer__meta"><span class="av-offer__v">' + esc(vname(p.variant)) + '</span><span class="av-offer__p">' + esc(p.price || '') + '</span></div>' +
        (p.available ? '' : '<span class="av-offer__out">' + esc(SOON.indexOf(p.handle) > -1 ? 'Coming soon' : (me.cfg.out || 'Not in stock just now')) + '</span>') +
        (p.why ? '<p class="av-offer__why" dir="auto">' + esc(p.why) + '</p>' : '') +
        '<div class="av-offer__act">' +
        (p.available && p.variant_id ? '<button class="av-offer__add" type="button" data-id="' + esc(p.variant_id) + '">' + esc(me.cfg.add || 'Add to basket') + '</button>' : '') +
        '<a class="av-offer__see" href="' + u + '">' + esc(me.cfg.view || 'View') + '</a>' +
        '</div></div>';
      row.appendChild(c);
    });
    row.addEventListener('click', function(e){ var b = e.target.closest('.av-offer__add'); if (b) me.add(b); });
    col.appendChild(row);
  };
  /* straight into the basket, and the header's count follows, as on the product page */
  Chat.prototype.add = function(b){
    var me = this;
    if (b.disabled) return;
    if (b.classList.contains('is-added')) { location.href = '/cart'; return; }
    var label = b.textContent; b.disabled = true; b.textContent = me.cfg.adding || 'Adding…';
    fetch('/cart/add.js', { method:'POST', headers:{ 'Content-Type':'application/json', Accept:'application/json' },
      body:JSON.stringify({ items:[{ id:Number(b.getAttribute('data-id')), quantity:1 }] }) })
      .then(function(r){ if (!r.ok) throw new Error('add'); return r.json(); })
      .then(function(){ return fetch('/cart.js').then(function(r){ return r.json(); }); })
      .then(function(cart){
        document.querySelectorAll('[data-cart-count],.cart-count,.js-cart-count').forEach(function(c){ c.textContent = cart.item_count; c.removeAttribute('hidden'); });
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles:true, detail:cart }));
        b.disabled = false; b.classList.add('is-added'); b.textContent = me.cfg.added || 'View basket';
      })
      .catch(function(){ b.disabled = false; b.textContent = label; });
  };
  /* the next step, ready to tap: shown above the box she is answered in */
  Chat.prototype.suggest = function(list){
    if (!this.chips) return;
    list = (Array.isArray(list) ? list : []).filter(Boolean).slice(0, 3);
    this.chips.innerHTML = list.map(function(r){ return '<button class="av__chip" type="button" dir="auto">' + esc(r) + '</button>'; }).join('');
    this.root.classList.toggle('has-replies', list.length > 0);
  };
  /* what her answer names is laid under it: the objects (a card each) and the scents (by name) */
  Chat.prototype.lay = function(col, text, animate, d, scentsOnly){
    var cfg = this.cfg, n = norm(text), found = [], seen = {}, scents = [];
    /* in the order the answer names them: a full name first, then a distinctive word of it */
    (cfg.products || []).forEach(function(p){
      var at = -1, i = n.indexOf('/products/' + p.h);
      if (i >= 0) at = i; else if ((i = n.indexOf(norm(p.t))) >= 0) at = i;
      else (p.k || []).forEach(function(k){ var m = new RegExp('(^|[^a-z])' + k + '([^a-z]|$)').exec(n); if (m && (at < 0 || m.index < at)) at = m.index + 10000; });
      if (at >= 0 && !seen[p.h]) { seen[p.h] = 1; found.push({ p:p, at:at }); }
    });
    found = found.sort(function(a, b){ return a.at - b.at; }).slice(0, 3).map(function(f){ return f.p; });
    SCENTS.forEach(function(s){ if (new RegExp('(^|[^a-z])' + s.k + '([^a-z]|$)').test(n)) scents.push(s); });
    if (scentsOnly) found = [];
    if (found.length) {
      var g = document.createElement('div'); g.className = 'av-cards';
      found.forEach(function(p, i){
        var a = document.createElement('a'); a.className = 'av-card'; a.href = p.u; a.style.setProperty('--d', (d + i * 140) + 'ms');
        a.innerHTML = '<span class="av-card__img">' + (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy">' : '') + '</span>' +
          '<span class="av-card__b"><span class="av-card__n">' + esc(p.t) + '</span>' + (p.p ? '<span class="av-card__p">' + esc(p.p) + '</span>' : '') + '</span>';
        if (!animate) a.style.animation = 'none', a.style.opacity = 1, a.style.transform = 'none';
        g.appendChild(a);
      });
      col.appendChild(g);
    }
    if (scents.length) {
      var row = document.createElement('div'); row.className = 'av-scents';
      scents.forEach(function(s, i){
        var a = document.createElement('a'); a.className = 'av-scent'; a.href = '/pages/' + s.k;
        a.style.setProperty('--d', (d + (found.length + i) * 110) + 'ms');
        a.innerHTML = esc(s.n) + ' <small>' + esc(s.f) + '</small>';
        if (!animate) a.style.animation = 'none', a.style.opacity = 1;
        row.appendChild(a);
      });
      col.appendChild(row);
    }
  };

  /* a question: what is written in the box — or words already said (a tap on a next step, or what she heard);
     { voice:true }: she answers aloud (they are talking with her); { spoken:true, q }: these words were said, and q is
     the voice note already on the screen */
  Chat.prototype.ask = function(said, opts){
    var me = this, typed = typeof said !== 'string', text = typed ? this.input.value.trim() : String(said).trim();
    opts = opts || {};
    if (!text || this.busy || !this.live) return;
    var aloud = !!opts.voice && !!this.player; this.vmode = aloud;
    this.hush(); this.hurry();
    this.root.classList.remove('has-replies');
    this.busy = true; this.send.disabled = true;
    if (typed) { this.input.value = ''; this.grow(); this.root.classList.remove('has-text'); }
    if (this.mode === 'page' && !matchMedia('(pointer:fine)').matches) this.input.blur();   /* on a phone, the keyboard steps aside to read */
    var l = loadLog(), mine = { who:'you', text:text, t:Date.now() }; if (opts.spoken) mine.v = 1; l.push(mine); saveLog(l);
    this.talking(true);
    var q = opts.q || this.mine(text, true, !!opts.spoken);
    var g = this.her(), dots = this.typing(g);
    requestAnimationFrame(function(){ me.reveal(g.el, 'end', true); });
    this.reply(text, l, aloud).then(function(a){
      var ll = loadLog(); ll.push({ who:'house', text:a.text, products:a.products, replies:a.replies, t:Date.now() }); saveLog(ll);
      var sc = me.tone(a.text, a.products); if (sc) me.paint(sc);
      var answer = function(sp){
        dots.remove();
        paras(a.text).forEach(function(p){ me.bubble(g, p); });
        if (sp) me.begin(sp, g, a);                              /* aloud: her words appear as she says them */
        else {
          var n = 0; if (!REDUCED) n = words(g.col);
          me.show(g.col, a.text, a.products, true, Math.min(3200, n * 26) + 200);
          me.suggest(a.replies);
        }
        requestAnimationFrame(function(){ me.settle(q, g.el, true); });
        me.busy = false; me.send.disabled = !me.input.value.trim() && !(me.speaking && me.speaking.on);   /* speaking: the button is her stop */
        if (me.mode === 'page' && matchMedia('(pointer:fine)').matches) me.input.focus();
      };
      /* her voice first, if it comes: she keeps typing until her first words are ready */
      if (aloud && a.voice && a.voice.id && a.voice.parts && a.voice.parts.length) {
        var sp = me.voiceOf(a.voice);
        sp.ready.then(function(ok){ answer(ok ? sp : null); });
      } else answer(null);
    }).catch(function(){
      dots.remove();
      me.bubble(g, me.cfg.error || 'Forgive me, I lost my words for a moment.');
      var b = document.createElement('button'); b.type = 'button'; b.className = 'av-retry'; b.textContent = me.cfg.retry || 'Ask again';
      b.addEventListener('click', function(){ g.el.remove(); q.remove(); if (opts.voice) me.unlock(); me.ask(text, { voice:!!opts.voice, spoken:!!opts.spoken }); });
      g.col.appendChild(b);
      var ll = loadLog(); ll.pop(); saveLog(ll);
      me.busy = false; me.send.disabled = !me.input.value.trim();
    });
  };
  /* she answers: through the webhook, or — only in preview — a sample of how it will look */
  Chat.prototype.reply = function(text, log, aloud){
    var cfg = this.cfg;
    if (!cfg.webhook) return new Promise(function(res){ setTimeout(function(){ res({ text:cfg.demo || '…' }); }, 2200); });
    /* the last few turns; what she showed is remembered by name, so "the gold one" still makes sense */
    var history = log.slice(-7, -1).map(function(m){
      var shown = (m.products || []).map(function(p){ return p.title + (p.variant ? ' (' + p.variant + ')' : ''); }).join('; ');
      return { role:m.who === 'you' ? 'user' : 'assistant', content:m.text + (shown ? '\n(Shown as cards: ' + shown + ')' : '') }; });
    var body = { action:'sendMessage', sessionId:sid(), chatInput:text, message:text, history:history, page:location.pathname, locale:navigator.language || 'en' };
    if (cfg.sendKey && !(cfg.sendKey in body)) body[cfg.sendKey] = text;
    if (aloud) body.voice = true;                              /* they spoke to her: she answers aloud */
    var ctl = window.AbortController ? new AbortController() : null, timer = ctl && setTimeout(function(){ ctl.abort(); }, 60000);
    return fetch(cfg.webhook, { method:'POST', headers:{ 'Content-Type':'application/json' }, body:JSON.stringify(body), signal:ctl ? ctl.signal : undefined })
      .then(function(r){ if (timer) clearTimeout(timer); if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function(raw){
        var out = raw, j = null, more = {};
        try { j = JSON.parse(raw); } catch (e) {}
        if (j !== null) {
          if (Array.isArray(j)) j = j[0];
          if (j && typeof j === 'object') { if (Array.isArray(j.products)) more.products = j.products; if (Array.isArray(j.replies)) more.replies = j.replies;
            if (j.voice && typeof j.voice === 'object') more.voice = j.voice; }
          var pick = function(o, path){ return String(path).split('.').reduce(function(a, k){ return a && typeof a === 'object' ? a[k] : undefined; }, o); };
          out = (cfg.replyKey && pick(j, cfg.replyKey)) || pick(j, 'output') || pick(j, 'reply') || pick(j, 'text') || pick(j, 'answer') ||
                pick(j, 'message') || pick(j, 'response') || pick(j, 'data.output') || (typeof j === 'string' ? j : '');
          if (out && typeof out === 'object') out = out.text || out.content || JSON.stringify(out);
        }
        out = String(out || '').trim();
        if (!out) throw new Error('empty');
        return { text:out, products:more.products, replies:more.replies || [], voice:more.voice };
      });
  };

  /* ── the panel on the shop and the product pages ─────────── */
  function launcher(){
    var pill = document.querySelector('.av-pill'), panel = document.querySelector('.av--panel');
    if (!pill || !panel) return;
    var chat = null, cfg = {};
    try { cfg = JSON.parse(panel.querySelector('[data-av-config]').textContent); } catch (e) {}
    /* the button appears only once she can answer — a customer never opens a conversation that cannot reply */
    var show = function(){ pill.hidden = false; };
    if (cfg.preview) show();
    else health(cfg.webhook, false).then(function(h){ if (isReady(h)) show(); });
    var open = function(){
      panel.hidden = false;
      if (!chat) chat = new Chat(panel);
      requestAnimationFrame(function(){ panel.classList.add('is-open'); });
      pill.classList.add('is-hidden'); panel.setAttribute('aria-hidden', 'false');
      setTimeout(function(){ if (matchMedia('(pointer:fine)').matches) chat.input.focus(); }, 350);
    };
    var close = function(){ panel.classList.remove('is-open'); pill.classList.remove('is-hidden'); panel.setAttribute('aria-hidden', 'true'); pill.focus(); };
    pill.addEventListener('click', open);
    panel.querySelector('.av__close').addEventListener('click', close);
    addEventListener('keydown', function(e){ if (e.key === 'Escape' && panel.classList.contains('is-open')) close(); });
    /* on a phone, the product page's buy bar rises from the foot: the pill rises above it */
    var bar = document.querySelector('.pdp__bar');
    if (bar) {
      var lift = function(){ var on = bar.classList.contains('on') && !bar.hidden && getComputedStyle(bar).display !== 'none';
        pill.style.setProperty('--av-lift', on ? bar.getBoundingClientRect().height + 'px' : '0px'); };
      new MutationObserver(lift).observe(bar, { attributes:true, attributeFilter:['class', 'hidden'] }); lift();
    }
  }

  function start(){
    document.querySelectorAll('.av--page').forEach(function(r){ new Chat(r); });
    launcher();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
